import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Check, Users } from "lucide-react";
import { useLanguage } from "../../LanguageProvider";
import "../../styles/components/modal.css";

export const MultiSelectionModal = ({
  isOpen,
  onClose,
  title,
  items, // Array of { id, name, profile_image_url, ... }
  selectedIds,
  onToggle,
  onConfirm,
  confirmText,
  searchPlaceholder,
  loading = false
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [items, searchTerm]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay-wrapper centered">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={onClose}
          />
          <div className="modal-container multi-select-container">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="confirm-modal multi-selection-modal"
            >
              <button className="modal-close-btn" onClick={onClose}>
                <X size={20} />
              </button>

              <div className="modal-header-section">
                <div className="modal-icon-pill">
                  <Users size={20} />
                </div>
                <h2 className="modal-title">{title}</h2>
              </div>

              <div className="modal-search-bar">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder={searchPlaceholder || t("search_players")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm("")}>
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="multi-selection-list">
                {filteredItems.length > 0 ? (
                  filteredItems.map(item => (
                    <label key={item.id} className="selection-list-item">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => onToggle(item.id)}
                      />
                      <div className="item-content">
                        <div className={`item-avatar ${item.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                          {item.profile_image_url ? (
                            <img src={item.profile_image_url} alt={item.name} />
                          ) : (
                            <span>{item.name.charAt(0).toUpperCase()}</span>
                          )}
                          {item.linked_user_id && (
                            <span className="verified-dot"><Check size={6} /></span>
                          )}
                        </div>
                        <span className="item-name">{item.name}</span>
                      </div>
                      <div className={`selection-check ${selectedIds.includes(item.id) ? 'active' : ''}`}>
                        <Check size={16} />
                      </div>
                    </label>
                  ))
                ) : (
                  <div className="no-results-state">
                    <Search size={32} />
                    <p>{t("no_results_found_for")} "{searchTerm}"</p>
                  </div>
                )}
              </div>

              <div className="modal-footer-actions">
                <button
                  className="modal-btn-secondary"
                  onClick={onClose}
                >
                  {t("cancel")}
                </button>
                <button
                  className="modal-btn-primary info"
                  disabled={selectedIds.length === 0 || loading}
                  onClick={onConfirm}
                >
                  {loading ? t("loading") : (confirmText || t("add"))} {selectedIds.length > 0 && !loading ? `(${selectedIds.length})` : ''}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};
